import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { notifications } from '@mantine/notifications';
import { IoClose } from 'react-icons/io5';
import { Modal, Button, ControlledInput, ControlledFileInput } from '@shared/ui';
import { useCreateBanner, useUpdateBanner } from '../../hooks/useBanners';
import type { Banner, CreateBannerDto } from '../../types';
import { bannerFormSchema } from '../../utils/bannerValidation';
import './CreateBannerModal.css';

interface CreateBannerModalProps {
    isOpen: boolean;
    onClose: () => void;
    banner?: Banner | null;
}
export interface BannerFormData {
    title: string;
    link?: string;  // только string, без undefined
    image: File | { id: number; url: string };
    storyImages?: Array<File | { id: number; url: string }>;
}

export default function CreateBannerModal({ isOpen, onClose, banner }: CreateBannerModalProps) {
    const isEditMode = !!banner;

    const { control, handleSubmit, formState: { errors }, reset, setValue } = useForm<BannerFormData>({
        resolver: yupResolver(bannerFormSchema),
        defaultValues: {
            title: banner?.title || '',
            link: banner?.link || '',
            storyImages: banner?.stories?.map(story => ({
                id: story.id,
                url: story.imageUrl
            })) || [],
            image: banner?.imageUrl ? { id: banner.id, url: banner.imageUrl } : undefined as any
        },
    });

    const createBanner = useCreateBanner();
    const updateBanner = useUpdateBanner();

    useEffect(() => {
        if (isOpen) {
            reset({
                title: banner?.title || '',
                link: banner?.link || '',
                storyImages: banner?.stories?.map(story => ({
                    id: story.id,
                    url: story.imageUrl
                })) || [],
                image: banner?.imageUrl ? { id: banner.id, url: banner.imageUrl } : undefined as any
            });
        }
    }, [isOpen, banner, reset]);

    const onSubmit = async (data: BannerFormData) => {
        try {
            // Извлекаем файлы из формы
            const imageFile = data.image instanceof File ? data.image : undefined;
            const storyFiles = data.storyImages
                ? data.storyImages.filter((img): img is File => img instanceof File)
                : undefined;

            if (isEditMode && banner) {
                const trimmedLink = data.link?.trim();
                const updateData = {
                    title: data.title,
                    // Если ссылка пустая, отправляем пустую строку явно, чтобы удалить её на сервере
                    link: trimmedLink === '' ? '' : (trimmedLink || undefined),
                };

                // Определяем удаленные истории
                // Получаем исходные ID историй из баннера
                const originalStoryIds = banner.stories?.map(story => story.id) || [];

                // Получаем текущие ID историй из data.storyImages (только те, которые не File, а объекты с id)
                const currentStoryIds = data.storyImages
                    ?.filter((img): img is { id: number; url: string } => !(img instanceof File) && 'id' in img)
                    .map((img) => img.id) || [];

                // Находим ID историй, которые были удалены (есть в исходных, но нет в текущих)
                const deletedStoryIds = originalStoryIds.filter(
                    id => !currentStoryIds.includes(id)
                );

                await updateBanner.mutateAsync({
                    id: banner.id,
                    data: updateData,
                    file: imageFile,
                    storyFiles: storyFiles && storyFiles.length > 0 ? storyFiles : undefined,
                    deletedStoryIds: deletedStoryIds.length > 0 ? deletedStoryIds : undefined,
                });

                notifications.show({
                    title: 'Успешно',
                    message: 'Баннер успешно обновлен',
                    color: 'green',
                });
            } else {
                const createData: CreateBannerDto = {
                    title: data.title,
                    link: data.link?.trim() || undefined,
                    isActive: true,
                };

                await createBanner.mutateAsync({
                    data: createData,
                    file: imageFile,
                    storyFiles: storyFiles && storyFiles.length > 0 ? storyFiles : undefined,
                });

                notifications.show({
                    title: 'Успешно',
                    message: 'Баннер успешно создан',
                    color: 'green',
                });
            }

            reset();
            onClose();
        } catch (error) {
            notifications.show({
                title: 'Ошибка',
                message: `Не удалось ${isEditMode ? 'обновить' : 'создать'} баннер`,
                color: 'red',
            });
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={isEditMode ? "Редактировать баннер" : "Добавить баннер"}
            size={500}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="create-banner-form">
                <div className="create-banner-form__content">
                    <div className="form-group">
                        <label htmlFor="title">Название *</label>
                        <ControlledInput
                            id="title"
                            name="title"
                            type="text"
                            control={control}
                            error={errors.title}
                        />
                    </div>

                    <div className="form-group">
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-start' }}>
                            <ControlledInput
                                label='Ссылка'
                                id="link"
                                name="link"
                                type="text"
                                control={control}
                                error={errors.link}
                                style={{ flex: 1 }}
                                placeholder="https://..."
                            />
                            <Button
                                variant="secondary-icon"
                                type="button"
                                onClick={() => setValue('link', '', { shouldValidate: true })}
                                aria-label="Очистить ссылку"
                                style={{ alignSelf: 'end' }}
                            >
                                <IoClose />
                            </Button>
                        </div>
                        <small className="form-hint">Внешнюю ссылку (https://...) можно ввести вручную — она откроется в браузере. Ссылку на категорию или подборку внутри приложения можно установить из их карточек после создания баннера</small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="image">Изображение баннера {!isEditMode && '*'}</label>
                        <ControlledFileInput
                            id="image"
                            name="image"
                            accept="image/*"
                            control={control}
                            error={errors.image}
                        />
                        <small className="form-hint">Рекомендуемый размер: 1080 × 400px или 1200 × 450px</small>
                    </div>

                    <div className="form-group">
                        <label htmlFor="storyImages">Изображения для историй</label>
                        <ControlledFileInput
                            id="storyImages"
                            name="storyImages"
                            accept="image/*"
                            multiple
                            control={control}
                            error={'storyImages' in errors ? errors.storyImages : undefined}
                        />
                        <small className="form-hint">Можно выбрать несколько изображений. Рекомендуемое соотношение: 9:16 (вертикальное), например 1080x1920px</small>
                    </div>
                </div>

                <div className="create-banner-form__footer">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={onClose}
                        className="create-banner-form__cancel-button"
                    >
                        Отмена
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isEditMode ? updateBanner.isPending : createBanner.isPending}
                        loading={isEditMode ? updateBanner.isPending : createBanner.isPending}
                        className="create-banner-form__submit-button"
                    >
                        {isEditMode ? 'Сохранить' : 'Создать'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}

